import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Virtuoso } from 'react-virtuoso';
import { Content } from 'src/contexts/ContentContext';
import { NO_GROUP_ID } from 'src/contexts/MultiSelectionContext';
import { useConfirmDialog } from 'src/core/interim/dialogs/ConfirmDialog/useConfirmDialog';
import { Stack } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { FieldError } from 'src/modals/common';
import { PurchaseEventItemHeader } from 'src/modals/common/Purchase/PurchaseEventItemHeader';
import { ContentId } from 'src/utils/constants/contentId';
import { formatCurrency } from 'src/utils/numberFormatter';
import { PurchaseOrderDetailsInput } from 'src/utils/purchaseUtils';
import { getTicketGroupInputTotalCost } from 'src/utils/ticketUtils';
import {
  Event,
  Feature,
  Performer,
  TicketGroupEditReason,
  TicketGroupInput,
  Venue,
} from 'src/WebApiController';

import { TicketGroupWithHandlers } from './configs/TicketGroupTableColumnsConfig';
import * as styles from './PurchaseTicketTable.css';
import {
  PurchaseEventItemHeaderWrapper,
  PurchaseTicketTableContainer,
} from './PurchaseTicketTable.styled';
import { TicketGroupTable } from './TicketGroupTable';

const VirtuoListThreshold = 5;
const VirtuosoOverScan = 600;

export function PurchaseTicketTable({
  currencyCode,
  disabled,
  ticketGroups,
  highlightTicketGroupIds,
  onTicketGroupAction: onTicketGroupActionProp,
  tgTableDisplayOnly,
}: {
  disabled?: boolean;
  currencyCode: string;
  ticketGroups?: TicketGroupInput[];
  highlightTicketGroupIds?: number[];
  tgTableDisplayOnly?: boolean;
} & Pick<TicketGroupWithHandlers, 'onTicketGroupAction'>) {
  const hasPlaceholderEventGroupFeature = useUserHasFeature(
    Feature.PlaceholderEventGroup
  );

  const { openConfirmDialog, closeConfirmDialog } = useConfirmDialog();

  // Wrap the raw action so that "Copy tickets" (event header or per ticket group)
  // first prompts the user to choose whether to split the source unit cost across
  // the events they copy into. The choice is threaded through as
  // `additionalData.splitCostAcrossEvents` for the event-search step to honor.
  const onTicketGroupAction = useMemo<
    TicketGroupWithHandlers['onTicketGroupAction']
  >(() => {
    if (!onTicketGroupActionProp) {
      return undefined;
    }
    return (params, showConfirmDialog) => {
      const { action, additionalData } = params;
      const isCopyTickets =
        action === TicketGroupEditReason.AddTickets &&
        Boolean(
          additionalData?.sourceTicketGroup ||
          additionalData?.sourceTicketGroups?.length
        );

      if (!isCopyTickets) {
        onTicketGroupActionProp(params, showConfirmDialog);
        return;
      }

      const proceed = (splitCostAcrossEvents: boolean) => {
        closeConfirmDialog();
        onTicketGroupActionProp(
          {
            ...params,
            additionalData: { ...additionalData, splitCostAcrossEvents },
          },
          showConfirmDialog
        );
      };

      openConfirmDialog({
        size: 's',
        headerText: <Content id={ContentId.SplitUnitCostQuestion} />,
        bodyText: <Content id={ContentId.SplitUnitCostMessage} />,
        okText: ContentId.Split,
        cancelText: ContentId.LeaveIt,
        onOkay: () => proceed(true),
        onCancel: () => proceed(false),
      });
    };
  }, [onTicketGroupActionProp, openConfirmDialog, closeConfirmDialog]);

  const { getFieldState } = useFormContext<PurchaseOrderDetailsInput>();
  const uniqueEvents = useMemo(
    () =>
      ticketGroups?.reduce(
        (events, tg) => {
          const eventId =
            tg.event?.virtId ??
            tg.viagogoEventId?.toString() ??
            tg.eventMappingId;
          if (eventId && !events[eventId]) {
            events[eventId] = {
              event: tg.event!,
              performer: tg.performer,
              venue: tg.venue,
            };
          }

          return events;
        },
        {} as Record<
          string,
          { event: Event; performer?: Performer | null; venue?: Venue | null }
        >
      ),
    [ticketGroups]
  );

  const events = useMemo(
    () =>
      Object.values(uniqueEvents || [])
        .filter((ev) => ev.event)
        .toSorted((a, b) => {
          const dateA = a.event.dates?.start;
          const dateB = b.event.dates?.start;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return new Date(dateA).getTime() - new Date(dateB).getTime();
        }),
    [uniqueEvents]
  );

  const eventTicketGroupsMap = useMemo(
    () =>
      new Map(
        events.map((ev) => {
          const groups = ticketGroups
            ?.map((tg, i) => ({ tg, i }))
            .filter((tg) => tg.tg.event?.virtId === ev.event.virtId);
          return [ev.event.virtId!, groups ?? []];
        })
      ),
    [events, ticketGroups]
  );

  const onCopyTicketsForEvent = useMemo<
    ((eventTicketGroups: TicketGroupInput[]) => void) | undefined
  >(() => {
    if (!hasPlaceholderEventGroupFeature || !onTicketGroupAction) {
      return undefined;
    }
    return (eventTicketGroups: TicketGroupInput[]) => {
      onTicketGroupAction({
        ticketGroupId: -1,
        action: TicketGroupEditReason.AddTickets,
        additionalData: {
          sourceTicketGroups: eventTicketGroups,
        },
      });
    };
  }, [hasPlaceholderEventGroupFeature, onTicketGroupAction]);

  const itemContent = useCallback(
    (
      _index: number,
      ev: { event: Event; performer?: Performer | null; venue?: Venue | null }
    ) => {
      const eventTicketGroups = eventTicketGroupsMap.get(ev.event.virtId!);
      const totalCost =
        eventTicketGroups?.reduce((sum, tg) => {
          sum += getTicketGroupInputTotalCost(tg.tg);
          return sum;
        }, 0) || 0;

      const ticketGroups = eventTicketGroups?.map((tg) => tg.tg) ?? [];

      return (
        <PurchaseTicketTableContainer key={ev.event.virtId}>
          <PurchaseEventItemHeaderWrapper>
            <PurchaseEventItemHeader
              removeMetrics
              disabled={disabled}
              event={ev.event}
              performer={ev.performer}
              venue={ev.venue}
              hideYearFromDate
              numOfTicketGroups={eventTicketGroups?.length || 0}
              numOfTickets={(eventTicketGroups ?? []).reduce(
                (r, tg) => r + (tg.tg.numberOfTickets?.value ?? 0),
                0
              )}
              cost={formatCurrency(totalCost, currencyCode)}
              onAddTicketsForEvent={
                onTicketGroupAction
                  ? (e, p, v) => {
                      onTicketGroupAction({
                        ticketGroupId: -1,
                        action: TicketGroupEditReason.AddTickets,
                        additionalData: {
                          event: e,
                          performer: p,
                          venue: v,
                        },
                      });
                    }
                  : undefined
              }
              onCopyTicketsForEvent={() =>
                onCopyTicketsForEvent?.(ticketGroups)
              }
              showEventStatus
            />
          </PurchaseEventItemHeaderWrapper>
          <div className={styles.purchaseTicketGroupsWrapper}>
            <TicketGroupTable
              groupId={NO_GROUP_ID}
              disabled={disabled}
              entities={eventTicketGroups?.map((tg) => tg.tg) ?? []}
              entityCount={eventTicketGroups?.length ?? 0}
              highlightTicketGroupIds={highlightTicketGroupIds}
              onTicketGroupAction={onTicketGroupAction}
              event={ev.event}
              performer={ev.performer}
              venue={ev.venue}
              getDataFail={false}
              displayOnly={tgTableDisplayOnly}
            />
            <Stack direction="column">
              {eventTicketGroups?.flatMap((tg) => {
                const errors = [];
                const tgFieldState = getFieldState(`ticketGroups.${tg.i}`);
                if (tgFieldState.error?.message) {
                  errors.push(
                    <FieldError key={tg.i}>
                      {tgFieldState.error.message}
                    </FieldError>
                  );
                }
                tg.tg.tickets?.value?.forEach((_, j) => {
                  const seatField = getFieldState(
                    `ticketGroups.${tg.i}.tickets.value.${j}.seat`
                  );
                  if (seatField.error?.message) {
                    errors.push(
                      <FieldError key={`${tg.i}-${j}`}>
                        {seatField.error.message}
                      </FieldError>
                    );
                  }
                });

                return errors;
              })}
            </Stack>
          </div>
        </PurchaseTicketTableContainer>
      );
    },
    [
      currencyCode,
      disabled,
      eventTicketGroupsMap,
      getFieldState,
      highlightTicketGroupIds,
      onCopyTicketsForEvent,
      onTicketGroupAction,
      tgTableDisplayOnly,
    ]
  );

  const useVirtuoso = useMemo(
    () => events.length >= VirtuoListThreshold,
    [events.length]
  );

  if (!events.length) {
    return <Content id={ContentId.None} />;
  }

  if (useVirtuoso) {
    return (
      <Virtuoso
        className={styles.eventPurchaseOrderVirtuoso}
        data={events}
        itemContent={itemContent}
        overscan={VirtuosoOverScan}
      />
    );
  }

  return (
    <>
      {events
        .filter((ev) => ev.event)
        .map((ev, index) => itemContent(index, ev))}
    </>
  );
}
